/**
 * Painel "⚡ Acionamentos manuais" — réplica do `buildQuickActions` do
 * protótipo v5.10. Componente **reutilizável e presentacional** (sem hooks,
 * sem datasource): renderiza os cards e emite `onAcionar(code)`. Cada tela
 * decide o que fazer com a intenção (M3 abre o confirm de barragem; o
 * dashboard futuro fará o mesmo — regra de reutilização do usuário).
 */
import type { TarpCode } from '@/domain';
import { QuickActionIcon } from './QuickActionIcon';
import { QUICK_ACTIONS, type QuickAction } from './quick-actions';

export interface AcionamentosManuaisProps {
  /** Intenção emitida ao clicar num card. A tela hospedeira decide o efeito. */
  onAcionar: (code: TarpCode) => void;
  /** Sobrescreve os acionamentos (default = os 5 do protótipo v5.10). */
  actions?: readonly QuickAction[];
  title?: string;
  subtitle?: string;
  className?: string;
}

export function AcionamentosManuais({
  onAcionar,
  actions = QUICK_ACTIONS,
  title = 'Acionamentos manuais',
  subtitle = 'Acione diretamente o fluxo de resposta conforme o que está acontecendo.',
  className = '',
}: AcionamentosManuaisProps) {
  return (
    <section className={`rounded-xl border border-jf-border bg-jf-surface p-4 ${className}`}>
      <div className="mb-1 flex items-center gap-2">
        <QuickActionIcon name="bolt" size={16} />
        <h2 className="text-sm font-bold text-white">{title}</h2>
      </div>
      <p className="mb-3 text-[11px] text-jf-dim">{subtitle}</p>

      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <button
            key={a.code}
            type="button"
            onClick={() => onAcionar(a.code)}
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-[1.5px] p-3.5 text-center transition hover:brightness-125"
            style={{
              flex: '1 1 130px',
              minWidth: 130,
              maxWidth: 'calc(20% - 7px)',
              borderColor: `${a.color}44`,
              background: `${a.color}11`,
            }}
          >
            <span
              className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border"
              style={{ borderColor: `${a.color}55`, background: `${a.color}22`, color: a.color }}
            >
              <QuickActionIcon name={a.iconName} size={22} color={a.color} />
            </span>
            <span className="text-[11px] font-extrabold leading-tight text-slate-200">{a.label}</span>
            <span className="text-[10px] leading-tight text-jf-muted">{a.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
